import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  Calendar,
  Moon,
  Sun,
  ChevronLeft,
  ChevronRight,
  Bell,
  Wallet,
  X,
  LogOut
} from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';
import { useState, useEffect } from 'react';
import { cn } from '../lib/utils';
import { getTeachers, getPendingWithdrawalsCount } from '../lib/api';
import { toast } from 'sonner';



export function Sidebar({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) {
  const { theme, toggleTheme } = useTheme();
  const [unverifiedCount, setUnverifiedCount] = useState(0);
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [teachersRes, withdrawalsRes] = await Promise.all([
          getTeachers(),
          getPendingWithdrawalsCount()
        ]);
        const unverified = teachersRes.data.filter(t => !t.isVerified).length;
        setUnverifiedCount(unverified);
        setPendingWithdrawals(withdrawalsRes.data.count || 0);
      } catch (error) {
        console.error('Error fetching counts:', error);
      }
    };
    fetchCounts();
    // Poll every 30 seconds for updates
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/teachers', label: 'Teachers', icon: Users, badge: unverifiedCount },
    { path: '/students', label: 'Students', icon: GraduationCap },
    { path: '/bookings', label: 'Bookings', icon: Calendar },
    { path: '/withdrawals', label: 'Withdrawals', icon: Wallet, badge: pendingWithdrawals },
  ];

  const totalNotifications = unverifiedCount + pendingWithdrawals;

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "fixed left-0 top-0 h-screen z-40 flex flex-col",
        "bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl shadow-2xl lg:shadow-none",
        "border-r border-slate-200/60 dark:border-slate-700/60",
        "transition-all duration-300 ease-in-out",
        "w-64 lg:w-auto", // Fixed width on mobile, responsive on lg
        collapsed ? "lg:w-20" : "lg:w-64",
        mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}
    >
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-slate-200/60 dark:border-slate-700/60">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center shadow-lg">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            {(!collapsed || mobileOpen) && (
              <div className="flex flex-col">
                <span className="font-bold text-slate-900 dark:text-white tracking-tight">Find Teacher</span>
                <span className="text-xs text-slate-500 dark:text-slate-400">Admin Panel</span>
              </div>
            )}
          </div>
          {/* Close button on mobile */}
          <button
            className="lg:hidden p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            onClick={() => setMobileOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Notification Alert for Pending Actions */}
      {totalNotifications > 0 && (!collapsed || mobileOpen) && (
        <div className="mx-3 mt-4 space-y-2">
          {unverifiedCount > 0 && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-amber-600 dark:text-amber-400 animate-pulse" />
                <span className="text-xs font-medium text-amber-800 dark:text-amber-300">
                  {unverifiedCount} teacher{unverifiedCount > 1 ? 's' : ''} pending verification
                </span>
              </div>
            </div>
          )}
          {pendingWithdrawals > 0 && (
            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl">
              <div className="flex items-center gap-2">
                <Wallet className="w-4 h-4 text-emerald-600 dark:text-emerald-400 animate-pulse" />
                <span className="text-xs font-medium text-emerald-800 dark:text-emerald-300">
                  {pendingWithdrawals} withdrawal{pendingWithdrawals > 1 ? 's' : ''} pending approval
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            data-testid={`nav-${item.label.toLowerCase()}`}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium",
                "transition-all duration-200 relative",
                isActive
                  ? "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 shadow-sm"
                  : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-slate-900 dark:hover:text-slate-200"
              )
            }
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {(!collapsed || mobileOpen) && <span>{item.label}</span>}
            {item.badge > 0 && (
              <span className={cn(
                "flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-bold rounded-full",
                "bg-amber-500 text-white",
                (collapsed && !mobileOpen) ? "absolute -top-1 -right-1" : "ml-auto"
              )}>
                {item.badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-slate-200/60 dark:border-slate-700/60 space-y-2">
        {/* Theme Toggle */}
        <Button
          data-testid="theme-toggle-btn"
          variant="ghost"
          onClick={toggleTheme}
          className={cn(
            "w-full justify-start gap-3 px-3 py-2.5 h-auto",
            "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
          )}
        >
          {theme === 'dark' ? (
            <>
              <Sun className="w-5 h-5" />
              {(!collapsed || mobileOpen) && <span>Light Mode</span>}
            </>
          ) : (
            <>
              <Moon className="w-5 h-5" />
              {(!collapsed || mobileOpen) && <span>Dark Mode</span>}
            </>
          )}
        </Button>

        <Button
          data-testid="logout-btn"
          variant="ghost"
          onClick={handleLogout}
          className={cn(
            "w-full justify-start gap-3 px-3 py-2.5 h-auto",
            "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
          )}
        >
          <LogOut className="w-5 h-5" />
          {(!collapsed || mobileOpen) && <span>Logout</span>}
        </Button>

        {/* Collapse Toggle */}
        <Button
          data-testid="sidebar-collapse-btn"
          variant="ghost"
          onClick={() => setCollapsed(!collapsed)}
          className={cn(
            "w-full justify-start gap-3 px-3 py-2.5 h-auto hidden lg:flex",
            "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
          )}
        >
          {collapsed ? (
            <>
              <ChevronRight className="w-5 h-5" />
            </>
          ) : (
            <>
              <ChevronLeft className="w-5 h-5" />
              <span>Collapse</span>
            </>
          )}
        </Button>
      </div>
    </aside>
  );
}
